import React, { useEffect, useRef, useState } from 'react';

// Set REACT_APP_GOOGLE_CLIENT_ID (Web client ID from Google Cloud Console) to enable this.
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let scriptPromise = null;
function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Renders Google's official "Sign in with Google" button. Calls onCredential(idToken)
// with the returned Google ID token. Renders nothing when the client ID isn't configured.
export default function GoogleSignInButton({ onCredential, text = 'continue_with' }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return undefined;
    let cancelled = false;
    loadGis().then((ok) => {
      if (cancelled) return;
      if (!ok || !window.google?.accounts?.id) { setFailed(true); return; }
      try {
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => { if (resp?.credential) onCredential(resp.credential); },
        });
        if (ref.current) {
          ref.current.innerHTML = '';
          window.google.accounts.id.renderButton(ref.current, {
            theme: 'outline', size: 'large', text, shape: 'rectangular',
            logo_alignment: 'center', width: Math.min(ref.current.offsetWidth || 320, 400),
          });
          // If GIS rendered nothing (domain not in allowlist), show fallback after short delay
          setTimeout(() => {
            if (!cancelled && ref.current && ref.current.childElementCount === 0) setFailed(true);
          }, 800);
        }
      } catch (_) { setFailed(true); }
    });
    return () => { cancelled = true; };
  }, [onCredential, text]);

  if (!CLIENT_ID) return null;
  if (failed) return (
    <button type="button" disabled style={{ width: '100%', padding: '10px 16px', borderRadius: 8, border: '1.5px solid #D4D0C8', background: '#F5F5F5', cursor: 'not-allowed', fontFamily: 'inherit', fontSize: 14, color: '#A8A29E', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#A8A29E" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#A8A29E" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#A8A29E" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#A8A29E" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Google Sign-In unavailable
    </button>
  );
  return <div ref={ref} style={{ display: 'flex', justifyContent: 'center', minHeight: 40 }} />;
}
